import React from 'react'
import _ from 'underscore'
import { ArrowRight, BookOpen, Check, Circle, Hourglass, Route, Users } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import store from '../../state/store'
import Currency from '../currency'

// Vertical "journey" summary for an enquiry. Each stage shows where the
// enquiry currently sits, with a deep-link into the tab that owns that work.
class EnquiryOverview extends React.Component {
  goToTab = (value) => () => {
    if (value.indexOf('lp') === 0) {
      const lesson_plan_id = value.substring(2)
      store.emit('ui:enquiry:lesson_plan_id', lesson_plan_id)
    }
    store.emit('ui:enquiry:tab_key', value)
  }

  goToLessonPlanTab = (lesson_plan_id, lesson_plan_tab) => () => {
    const lp = _.find(this.props.lesson_plans || [], (x) => x.id === lesson_plan_id)
    const tutoring_job_id = lp && lp.tutoring_job && lp.tutoring_job.id

    // Order matters: `ui:enquiry:lesson_plan_id` resets `lesson_plan_tab` to
    // 'lesson_setup', so we emit `ui:lesson_plan:tab` after it.
    store.emit('ui:enquiry:lesson_plan_id', lesson_plan_id)
    store.emit('ui:lesson_plan:tab', lesson_plan_tab)
    store.emit('ui:enquiry:tab_key', `lp${lesson_plan_id}`)

    // Mirror lesson_plans/show.js onTabClick — the tab's data isn't loaded
    // until the matching fetch is dispatched.
    if (lesson_plan_tab === 'placement' && tutoring_job_id) {
      store.emit('tutoring_jobs:show', tutoring_job_id)
    } else if (lesson_plan_tab === 'tutors' && tutoring_job_id) {
      store.emit('tutoring_job:tutor_results:index', tutoring_job_id)
    } else if (lesson_plan_tab === 'lesson_pricing') {
      store.get().set({ change_applied: false })
      store.emit('lesson_plan:lesson_orders:index', lesson_plan_id)
    }
  }

  renderStageRow({ status, title, summary, action }) {
    const dot = STATUS_DOTS[status]
    const DotIcon = dot.icon
    return (
      <div className="flex items-start gap-4 py-4 first:pt-0 last:pb-0">
        <div className={`flex size-8 items-center justify-center rounded-full ${dot.classes}`}>
          <DotIcon className="size-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-sm font-semibold">{title}</div>
            {action ? (
              <a
                href={action.href}
                onClick={(e) => { e.preventDefault(); action.onClick() }}
                className="text-xs text-primary no-underline hover:underline"
              >
                {action.label} <ArrowRight className="ml-0.5 inline size-3" />
              </a>
            ) : null}
          </div>
          {summary ? <div className="mt-1 text-[13px] text-muted-foreground">{summary}</div> : null}
        </div>
      </div>
    )
  }

  tabHref(tab) {
    return `?e=${this.props.enquiry.id}&t=${tab}`
  }

  lessonPlanHref(lesson_plan_id, lesson_plan_tab) {
    return `?e=${this.props.enquiry.id}&t=lp${lesson_plan_id}&lpt=${lesson_plan_tab}`
  }

  customerStage(enquiry) {
    const customer = enquiry.customer
    const bits = _.compact([
      customer && customer.name,
      customer && customer.email_address,
      enquiry.country && enquiry.country.name,
    ])
    return {
      status: customer ? 'done' : 'pending',
      title: 'Customer',
      summary: bits.length ? bits.join(' · ') : 'No customer details',
    }
  }

  learnersStage(enquiry, learners) {
    const count = (learners || []).length
    if (count === 0) {
      return {
        status: 'pending',
        title: 'Learners',
        summary: 'No learners yet',
        action: { label: 'Add learners', href: this.tabHref('learners'), onClick: this.goToTab('learners') },
      }
    }
    const names = _.map(learners, (l) => l.name).filter(Boolean).join(', ')
    return {
      status: 'done',
      title: `Learners (${count})`,
      summary: names || `${count} learner${count === 1 ? '' : 's'} added`,
      action: { label: 'View learners', href: this.tabHref('learners'), onClick: this.goToTab('learners') },
    }
  }

  lessonPlansStage(lesson_plans) {
    const plans = lesson_plans || []
    if (plans.length === 0) {
      return {
        status: 'pending',
        title: 'Lesson Plans',
        summary: 'No lesson plans yet',
      }
    }
    return {
      status: 'done',
      title: `Lesson Plans (${plans.length})`,
      summary: _.map(plans, (lp) => {
        const subj = _.compact([lp.subject_level_name, lp.subject_category_name]).join(' ')
        return subj || `LP #${lp.id}`
      }).join(' · '),
    }
  }

  renderPerLessonPlanStages(lesson_plans) {
    return _.map(lesson_plans || [], (lp) => {
      const ref = (lp.tutoring_job && lp.tutoring_job.reference_number) || `LP #${lp.id}`
      const subj = _.compact([lp.subject_level_name, lp.subject_category_name]).join(' ')

      // Pricing — finalized when LessonOrders are in placed/provisioned state.
      const summary = lp.lesson_orders_summary || { total: 0, placed: 0, paid: 0, priced_amount: 0, paid_amount: 0, currency_code: null, payment_terms: [] }
      const shapeBits = _.compact([
        lp.lessons_per_week ? `${lp.lessons_per_week} lesson${lp.lessons_per_week === 1 ? '' : 's'}/week` : null,
        lp.lesson_duration ? `${lp.lesson_duration} min` : null,
        lp.number_of_months ? `${lp.number_of_months} month commitment` : null,
      ])
      const cadenceLabel = paymentCadenceLabel(summary.payment_terms)
      const priceTag = summary.priced_amount && summary.currency_code ? (
        <React.Fragment>
          {' · '}
          <Currency value={Number(summary.priced_amount)} currency={summary.currency_code} />
          {cadenceLabel ? ` ${cadenceLabel}` : null}
        </React.Fragment>
      ) : null

      let pricingStatus, pricingSummary
      if (summary.total === 0) {
        pricingStatus = 'pending'
        pricingSummary = shapeBits.length ? shapeBits.join(' · ') + ' · no lesson orders yet' : 'No lesson orders yet'
      } else {
        const allPriced = summary.placed === summary.total
        pricingStatus = allPriced ? 'done' : 'in_progress'

        // Most lesson plans have a single order, so collapse the "X of Y"
        // language down to "Priced" / "Not yet priced" in that case.
        const orderState = summary.total === 1
          ? (allPriced ? 'Priced' : 'Not yet priced')
          : (allPriced
              ? `All ${summary.total} orders priced`
              : `${summary.placed} of ${summary.total} orders priced`)

        pricingSummary = (
          <React.Fragment>
            {shapeBits.length ? <div>{shapeBits.join(' · ')}</div> : null}
            <div>{orderState}{priceTag}</div>
          </React.Fragment>
        )
      }

      const positions = (lp.tutoring_job && lp.tutoring_job.tutoring_job_positions) || []
      const proposals = (lp.tutoring_job && lp.tutoring_job.tutor_proposals) || []
      const selected = positions.filter((p) => p.tutor_selection_tutor_id).length

      let tutorStatus, tutorSummary
      if (selected > 0 && positions.length > 0 && selected === positions.length) {
        tutorStatus = 'done'
        tutorSummary = `All ${positions.length} position${positions.length === 1 ? '' : 's'} filled`
      } else if (selected > 0) {
        tutorStatus = 'in_progress'
        tutorSummary = `${selected} of ${positions.length || selected} position${positions.length === 1 ? '' : 's'} filled`
      } else if (proposals.length > 0) {
        tutorStatus = 'in_progress'
        tutorSummary = `${proposals.length} tutor${proposals.length === 1 ? '' : 's'} proposed, awaiting selection`
      } else if (positions.length > 0) {
        tutorStatus = 'in_progress'
        tutorSummary = `${positions.length} position${positions.length === 1 ? '' : 's'} open, no tutors proposed`
      } else {
        tutorStatus = 'pending'
        tutorSummary = 'Placement not started'
      }

      return (
        <div key={lp.id} className="ml-4 mt-4 border-l-2 border-border pl-4">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {subj ? `${subj} · ${ref}` : ref}
          </div>
          {this.renderStageRow({
            status: tutorStatus,
            title: 'Tutor Selection',
            summary: tutorSummary,
            action: {
              label: 'Open placement',
              href: this.lessonPlanHref(lp.id, 'placement'),
              onClick: this.goToLessonPlanTab(lp.id, 'placement'),
            },
          })}
          {this.renderStageRow({
            status: pricingStatus,
            title: 'Pricing',
            summary: pricingSummary,
            action: {
              label: 'Open pricing',
              href: this.lessonPlanHref(lp.id, 'lesson_pricing'),
              onClick: this.goToLessonPlanTab(lp.id, 'lesson_pricing'),
            },
          })}
        </div>
      )
    })
  }

  paymentStage(orders) {
    const list = orders || []
    const action = { label: 'View orders', href: this.tabHref('orders'), onClick: this.goToTab('orders') }
    if (list.length === 0) {
      return {
        status: 'pending',
        title: 'Payment',
        summary: 'No orders yet',
        action: { ...action, label: 'Orders' },
      }
    }
    const paid = list.filter((o) => o.paid)
    const totalPaid = _.reduce(paid, (acc, o) => acc + Number(o.total || o.amount || 0), 0)
    const currency = list[0] && list[0].currency_code
    const paidAmountTag = totalPaid && currency ? (
      <React.Fragment>
        {' · '}
        <Currency value={totalPaid} currency={currency} />
      </React.Fragment>
    ) : null

    if (paid.length === list.length) {
      return {
        status: 'done',
        title: 'Payment',
        summary: (
          <React.Fragment>
            {paid.length} order{paid.length === 1 ? '' : 's'} paid{paidAmountTag}
          </React.Fragment>
        ),
        action,
      }
    }
    return {
      status: 'in_progress',
      title: 'Payment',
      summary: (
        <React.Fragment>
          {paid.length} of {list.length} paid
          {list.length - paid.length ? ` · ${list.length - paid.length} outstanding` : ''}
          {paidAmountTag}
        </React.Fragment>
      ),
      action,
    }
  }

  closeStage(enquiry) {
    if (enquiry.handed_over) {
      return {
        status: 'done',
        title: 'Handed Over',
        summary: 'Enquiry has been handed over to support',
      }
    }
    if (enquiry.deactivated) {
      const reason = enquiry.deactivation_reason && enquiry.deactivation_reason.reason
      return {
        status: 'done',
        title: 'Closed',
        summary: reason ? `Deactivated: ${reason}` : 'Deactivated',
      }
    }
    const canClose = enquiry.actions && (enquiry.actions.can_handover || enquiry.actions.can_deactivate)
    return {
      status: 'pending',
      title: 'Close',
      summary: canClose ? 'Hand over to support or deactivate when done' : 'Available once placement is complete',
      action: canClose
        ? { label: 'Close enquiry', href: this.tabHref('close'), onClick: this.goToTab('close') }
        : null,
    }
  }

  render() {
    const enquiry = this.props.enquiry
    const learners = this.props.learners
    const lesson_plans = this.props.lesson_plans
    const orders = this.props.orders

    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Route className="size-4" />
              Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            {this.renderStageRow(this.customerStage(enquiry))}
            {this.renderStageRow(this.paymentStage(orders))}
            {this.renderStageRow(this.closeStage(enquiry))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-4" />
              Learners
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            {this.renderStageRow(this.learnersStage(enquiry, learners))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="size-4" />
              Lesson Plans
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {this.renderStageRow(this.lessonPlansStage(lesson_plans))}
            </div>
            {this.renderPerLessonPlanStages(lesson_plans)}
          </CardContent>
        </Card>
      </div>
    )
  }
}

// Map LessonOrder.payment_term distinct values into a short trailing label
// (e.g. "/month", "upfront"). Returns null when payment terms span multiple
// distinct values or aren't recognised, so the caller can omit the suffix.
function paymentCadenceLabel(payment_terms) {
  if (!payment_terms || payment_terms.length !== 1) return null
  const term = payment_terms[0]
  if (term === 'full_payment_upfront') return 'upfront'
  if (term && term.indexOf('monthly') === 0) return '/ month'
  return null
}

const STATUS_DOTS = {
  done:        { classes: 'bg-emerald-100 text-emerald-700',     icon: Check },
  in_progress: { classes: 'bg-amber-100 text-amber-700',         icon: Hourglass },
  pending:     { classes: 'bg-muted text-muted-foreground/60',   icon: Circle },
}

export default EnquiryOverview
